/**
 * SectionStateManager - Unified state persistence for accordion sections and toggles
 * Provides automatic saving/loading of UI state per chart with granular control
 */

import { devError } from '../../../../../utils/devLog';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../../utils/localStorageUtils';

export interface SectionState {
    accordionSections: string[]; // Array for easier storage/restoration
    toggleStates: Record<string, boolean>;
    tableStates: Record<string, any>; // For table-specific state (sorting, filters, etc.)
    scrollPositions: Record<string, number>;
    lastUpdated: number;
    version: string; // For future migration compatibility
    hasInteracted: boolean; // Flag to indicate user has interacted with this chart (not first visit)
}

export interface SectionStateHookReturn {
    state: SectionState | null;
    isLoading: boolean;
    error: string | null;

    // Accordion management
    saveAccordionState: (expandedSections: Set<string>) => void;
    loadAccordionState: () => Set<string>;

    // Toggle management
    saveToggleState: (toggleId: string, value: boolean) => void;
    getToggleState: (toggleId: string, defaultValue?: boolean) => boolean;

    // Table state management
    saveTableState: (tableId: string, state: any) => void;
    getTableState: (tableId: string, defaultState?: any) => any;

    // Scroll position management
    saveScrollPosition: (sectionId: string, position: number) => void;
    getScrollPosition: (sectionId: string) => number;

    // Utilities
    clearState: () => void;
    exportState: () => SectionState | null;
    importState: (state: SectionState) => void;
}

/**
 * Default section state
 */
const createDefaultSectionState = (): SectionState => ({
    accordionSections: [],
    toggleStates: {},
    tableStates: {},
    scrollPositions: {},
    lastUpdated: Date.now(),
    version: '1.0.0',
    hasInteracted: false  // New charts start as not interacted
});

/**
 * Main SectionStateManager class
 */
class SectionStateManager {
    private stateCache = new Map<string, SectionState>();
    private readonly STATE_VERSION = '1.0.0';

    /**
     * Generate storage key for section state
     */
    private getStateKey(chartId: string, sectionType: string): string {
        return `panchanga:${sectionType}:state:${chartId}`;
    }

    /**
     * Load section state from storage with caching
     */
    loadSectionState(chartId: string, sectionType: string): SectionState {
        const cacheKey = `${chartId}:${sectionType}`;

        // Check cache first
        if (this.stateCache.has(cacheKey)) {
            return this.stateCache.get(cacheKey)!;
        }

        try {
            const key = this.getStateKey(chartId, sectionType);
            const stored = getLocalStorageItem<SectionState | null>(key, null);

            if (stored && this.isValidState(stored)) {
                // Migrate if needed
                const migratedState = this.migrateState(stored);
                this.stateCache.set(cacheKey, migratedState);
                return migratedState;
            }

            // Return default state
            const defaultState = createDefaultSectionState();
            this.stateCache.set(cacheKey, defaultState);
            return defaultState;

        } catch (error) {
            devError(`SectionStateManager: Failed to load state for ${chartId}:${sectionType}:`, error);
            return createDefaultSectionState();
        }
    }

    /**
     * Save section state to storage and cache
     */
    saveSectionState(
        chartId: string,
        sectionType: string,
        updates: Partial<SectionState>
    ): void {
        try {
            const currentState = this.loadSectionState(chartId, sectionType);
            const newState: SectionState = {
                ...currentState,
                ...updates,
                lastUpdated: Date.now(),
                version: this.STATE_VERSION
            };

            const key = this.getStateKey(chartId, sectionType);
            setLocalStorageItem(key, newState);

            // Update cache
            const cacheKey = `${chartId}:${sectionType}`;
            this.stateCache.set(cacheKey, newState);

        } catch (error) {
            devError(`❌ SectionStateManager: Failed to save state for ${chartId}:${sectionType}:`, error);
            throw error;
        }
    }

    /**
     * Save accordion expanded sections
     */
    saveAccordionState(
        chartId: string,
        sectionType: string,
        expandedSections: Set<string>
    ): void {
        this.saveSectionState(chartId, sectionType, {
            accordionSections: Array.from(expandedSections),
            hasInteracted: true  // Mark as interacted when saving accordion state
        });
    }

    /**
     * Save toggle state
     */
    saveToggleState(
        chartId: string,
        sectionType: string,
        toggleId: string,
        value: boolean
    ): void {
        const currentState = this.loadSectionState(chartId, sectionType);

        this.saveSectionState(chartId, sectionType, {
            toggleStates: {
                ...currentState.toggleStates,
                [toggleId]: value
            }
        });
    }

    /**
     * Save table-specific state (sorting, filters, etc.)
     */
    saveTableState(
        chartId: string,
        sectionType: string,
        tableId: string,
        tableState: any
    ): void {
        const currentState = this.loadSectionState(chartId, sectionType);

        this.saveSectionState(chartId, sectionType, {
            tableStates: {
                ...currentState.tableStates,
                [tableId]: tableState
            }
        });
    }

    /**
     * Save scroll position for a section
     */
    saveScrollPosition(
        chartId: string,
        sectionType: string,
        sectionId: string,
        position: number
    ): void {
        const currentState = this.loadSectionState(chartId, sectionType);

        this.saveSectionState(chartId, sectionType, {
            scrollPositions: {
                ...currentState.scrollPositions,
                [sectionId]: position
            }
        });
    }

    /**
     * Clear all state for a chart/section
     */
    clearSectionState(chartId: string, sectionType: string): void {
        try {
            const key = this.getStateKey(chartId, sectionType);
            localStorage.removeItem(key);

            // Clear cache
            const cacheKey = `${chartId}:${sectionType}`;
            this.stateCache.delete(cacheKey);

        } catch (error) {
            devError(`❌ SectionStateManager: Failed to clear state for ${chartId}:${sectionType}:`, error);
            throw error;
        }
    }

    /**
     * Validate stored state structure
     */
    private isValidState(state: any): state is SectionState {
        return (
            state &&
            typeof state === 'object' &&
            Array.isArray(state.accordionSections) &&
            typeof state.toggleStates === 'object' &&
            typeof state.tableStates === 'object' &&
            typeof state.scrollPositions === 'object' &&
            typeof state.lastUpdated === 'number'
            // Note: hasInteracted is optional for backward compatibility
        );
    }

    /**
     * Migrate state from older versions
     */
    private migrateState(state: any): SectionState {
        // Handle migration from older versions if needed
        if (!state.version || state.version !== this.STATE_VERSION) {
            // Add new fields with defaults
            return {
                ...createDefaultSectionState(),
                ...state,
                version: this.STATE_VERSION
            };
        }

        return state;
    }


    /**
     * Get debug information
     */
    getDebugInfo(): Record<string, any> {
        return {
            cacheSize: this.stateCache.size,
            cachedStates: Array.from(this.stateCache.keys()),
            stateVersion: this.STATE_VERSION
        };
    }
}

// Singleton instance
export const sectionStateManager = new SectionStateManager();

export default sectionStateManager;